// Durable queue of writes that could not reach the server.
//
// The shop this app replaces runs entirely on paper, and connectivity at a
// kiosk is not something the shopkeeper can do anything about. So a write must
// never depend on the network being up at the moment it happens: it goes into
// this queue, the UI moves on, and the queue drains whenever a connection
// comes back.
//
// Two properties matter more than anything else here:
//
//   1. Replay is safe. Every queued create carries a client-generated UUID
//      which the server treats as an idempotency key, so sending the same
//      entry twice produces one record and one stock movement. That means we
//      can retry freely without reconciling.
//
//   2. Order is preserved. Entries drain strictly front-to-back, so a product
//      created offline exists before the sale referencing it is sent.
//
// localStorage rather than IndexedDB: it matches how the rest of the app
// already persists state (see AuthContext), and the volume is tiny -- a sale
// is well under 1KB, so a heavy day of trading is tens of KB against a ~5MB
// budget. The trade-off is that a Service Worker cannot read it, which is why
// syncing is driven by the app rather than by Background Sync (unsupported on
// iOS, and it would force IndexedDB for no practical gain here).

import axios from "axios";
import { toast } from "sonner";

const QUEUE_KEY = "pasal_outbox";
const FAILED_KEY = "pasal_outbox_failed";

const API_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const API = `${API_BASE}/api`;

// ---------------------------------------------------------------- storage

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Corrupt or truncated JSON: better to start clean than to throw on every
    // render and wedge the whole app.
    return [];
  }
}

function write(key, entries) {
  try {
    localStorage.setItem(key, JSON.stringify(entries));
    return true;
  } catch (err) {
    // Quota exceeded is the realistic failure. Report it rather than silently
    // dropping a sale the shopkeeper believes is recorded.
    console.error(`outbox: could not persist ${key}`, err);
    return false;
  }
}

export const getQueue = () => read(QUEUE_KEY);
export const getFailed = () => read(FAILED_KEY);
export const pendingCount = () => read(QUEUE_KEY).length;

// Lets React re-render when the queue changes; `storage` only fires in *other*
// tabs, so we dispatch our own event for this one.
const CHANGED = "pasal-outbox-changed";
const notify = () => window.dispatchEvent(new Event(CHANGED));

export function subscribe(fn) {
  window.addEventListener(CHANGED, fn);
  window.addEventListener("storage", fn);
  return () => {
    window.removeEventListener(CHANGED, fn);
    window.removeEventListener("storage", fn);
  };
}

// Fired after a drain that actually sent something. Pages listen so their
// figures refresh: a sale that syncs in the background changes stock on the
// server, and without this the screen keeps showing pre-sync quantities until
// the shopkeeper happens to navigate.
const SYNCED = "pasal-outbox-synced";

export function onSynced(fn) {
  window.addEventListener(SYNCED, fn);
  return () => window.removeEventListener(SYNCED, fn);
}

// ---------------------------------------------------------------- queueing

export function enqueue({ method, path, body, label }) {
  const entry = {
    key: crypto.randomUUID(),
    method,
    path,
    body,
    label, // human-readable, for the pending/failed list
    queuedAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
  };
  const ok = write(QUEUE_KEY, [...read(QUEUE_KEY), entry]);
  notify();
  if (!ok) throw new Error("Could not save offline - storage full");
  return entry;
}

/**
 * Decide whether a failed request belongs in the queue, and queue it if so.
 *
 * Returns true when the write has been taken over by the outbox, so the caller
 * can carry on as if it succeeded -- from the shopkeeper's side it did: the
 * item is on the shelf, the money is in the till.
 *
 * A 4xx means the server understood and refused, which will not become true
 * later, so those are handed back to the caller to report. Anything else (no
 * response at all, or a 5xx) is the connection.
 */
export function queueIfOffline(err, { method, path, body, label }) {
  const status = err?.response?.status;
  if (status && status >= 400 && status < 500) return false;

  try {
    enqueue({ method, path, body, label });
    toast.success("Saved offline / अफलाइन सुरक्षित — sync हुनेछ");
    return true;
  } catch (storageErr) {
    // Only when localStorage itself refused. Let the caller show its own
    // error rather than pretend this was saved.
    console.error("Could not queue write:", storageErr);
    return false;
  }
}

function replaceQueue(entries) {
  write(QUEUE_KEY, entries);
  notify();
}

function moveToFailed(entry, reason) {
  write(FAILED_KEY, [...read(FAILED_KEY), { ...entry, failedAt: new Date().toISOString(), reason }]);
}

export function retryFailed(key) {
  const failed = read(FAILED_KEY);
  const entry = failed.find((e) => e.key === key);
  if (!entry) return;
  write(FAILED_KEY, failed.filter((e) => e.key !== key));
  write(QUEUE_KEY, [...read(QUEUE_KEY), { ...entry, attempts: 0, lastError: null }]);
  notify();
}

export function discardFailed(key) {
  write(FAILED_KEY, read(FAILED_KEY).filter((e) => e.key !== key));
  notify();
}

// ---------------------------------------------------------------- draining

let draining = false;

/**
 * Send queued entries in order. Returns {sent, failed, stopped}.
 *
 * Stops at the first network error and keeps the rest queued -- pushing on
 * would reorder writes and break the create-then-reference guarantee.
 */
export async function drain(getAuthHeader) {
  if (draining) return { sent: 0, failed: 0, stopped: true, reason: "busy" };
  draining = true;

  let sent = 0;
  let failed = 0;

  try {
    // Re-read each iteration: a sale may be queued while we are draining.
    while (true) {
      const queue = read(QUEUE_KEY);
      if (queue.length === 0) break;

      const entry = queue[0];
      try {
        await axios({
          method: entry.method,
          url: `${API}${entry.path}`,
          data: entry.body,
          ...getAuthHeader(),
        });
        replaceQueue(read(QUEUE_KEY).filter((e) => e.key !== entry.key));
        sent += 1;
      } catch (err) {
        const status = err.response?.status;

        if (status === 401 || status === 403) {
          // Token expired or revoked. Not the entry's fault and not something
          // retrying will fix right now -- leave the queue intact for after
          // the next login rather than burning through it.
          return { sent, failed, stopped: true, reason: "auth" };
        }

        if (status && status >= 400 && status < 500) {
          // Permanent: malformed, or referencing something now deleted.
          // Retrying forever would block every later entry, so set it aside
          // where a human can see it.
          const reason =
            err.response?.data?.detail || `Rejected by server (${status})`;
          moveToFailed(entry, reason);
          replaceQueue(read(QUEUE_KEY).filter((e) => e.key !== entry.key));
          failed += 1;
          continue;
        }

        // No response at all, or a 5xx: still offline, or the server is
        // unwell. Keep everything and try again later.
        replaceQueue(
          read(QUEUE_KEY).map((e) =>
            e.key === entry.key
              ? { ...e, attempts: e.attempts + 1, lastError: err.message }
              : e,
          ),
        );
        return { sent, failed, stopped: true, reason: "unreachable" };
      }
    }
  } finally {
    draining = false;
    if (sent > 0) window.dispatchEvent(new Event(SYNCED));
  }

  return { sent, failed, stopped: false, reason: null };
}
